import {
  PSEUDO_CALENDARS,
  type CalendarResponse,
  type OccurrenceResponse,
} from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { Button } from '@/components/ui';

interface CalendarOption {
  readonly id: string;
  readonly name: string;
  readonly color: string;
}

/** The real calendars plus, only when the current view actually has
 *  something from that source, the two fixed pseudo-calendars — a Events
 *  or Groups toggle with nothing to show is a control for nothing. */
function buildOptions(
  calendars: readonly CalendarResponse[],
  occurrences: readonly OccurrenceResponse[],
  t: (key: string) => string,
): CalendarOption[] {
  const options: CalendarOption[] = calendars.map((c) => ({
    id: c.id,
    name: c.name,
    color: c.color,
  }));

  if (occurrences.some((o) => o.source === 'EVENT')) {
    options.push({
      id: PSEUDO_CALENDARS.EVENT.id,
      name: t('calendar.pseudo.events'),
      color: PSEUDO_CALENDARS.EVENT.color,
    });
  }
  if (occurrences.some((o) => o.source === 'GROUP')) {
    options.push({
      id: PSEUDO_CALENDARS.GROUP.id,
      name: t('calendar.pseudo.groups'),
      color: PSEUDO_CALENDARS.GROUP.color,
    });
  }

  return options;
}

export function CalendarSidebar({
  calendars,
  occurrences,
  hiddenCalendarIds,
  onToggle,
  onAddEvent,
  onManage,
}: {
  readonly calendars: readonly CalendarResponse[];
  readonly occurrences: readonly OccurrenceResponse[];
  readonly hiddenCalendarIds: ReadonlySet<string>;
  readonly onToggle: (calendarId: string) => void;
  readonly onAddEvent?: () => void;
  readonly onManage?: () => void;
}) {
  const { t } = useI18n();
  const options = buildOptions(calendars, occurrences, t);

  return (
    <aside className="calendar-sidebar">
      {onAddEvent && (
        <Button type="button" variant="primary" className="calendar-add-event" onClick={onAddEvent}>
          {t('calendar.event.add')}
        </Button>
      )}

      <div className="calendar-sidebar-section">
        <div className="calendar-sidebar-section-header">
          <h2>{t('calendar.calendar.title')}</h2>
          {onManage && (
            <button
              type="button"
              className="calendar-manage-btn"
              onClick={onManage}
              aria-label={t('calendar.calendar.manage')}
              title={t('calendar.calendar.manage')}
            >
              ⚙
            </button>
          )}
        </div>

        <ul className="calendar-toggle-list">
          {options.map((option) => (
            <li key={option.id}>
              <label className="calendar-toggle">
                <input
                  type="checkbox"
                  checked={!hiddenCalendarIds.has(option.id)}
                  onChange={() => onToggle(option.id)}
                />
                <span className="calendar-toggle-dot" style={{ backgroundColor: option.color }} />
                <span className="calendar-toggle-name">{option.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
